import { useMutation, useQuery } from '@tanstack/react-query';
import type { SharePointCheckResponse, SharePointStatusResponse } from '@k-management/shared';
import { useState } from 'react';
import { apiRequest } from '../../lib/api.js';

/**
 * Administrators: the connection of this environment to the documents on
 * SharePoint (docs/adr/ADR-009). It shows who the server is for Microsoft, with
 * the values to enter in the federated credential of the Entra registration,
 * and checks the path up to the library, reading only.
 */
export function SharePointPage() {
  const status = useQuery({
    queryKey: ['sharepoint'],
    queryFn: () => apiRequest<SharePointStatusResponse>('/sharepoint'),
  });
  const check = useMutation({
    mutationFn: () => apiRequest<SharePointCheckResponse>('/sharepoint/check', { method: 'POST' }),
  });
  const data = status.data;

  return (
    <section className="stack">
      <div className="page-header">
        <div className="stack tight">
          <h1>SharePoint</h1>
          <p className="muted">
            Collegamento di questo ambiente ai documenti nel sito K-Apps (ADR-009). La verifica
            legge soltanto: non scrive nulla.
          </p>
        </div>
      </div>
      {status.isPending && <p className="muted">Caricamento…</p>}
      {status.isError && <p className="error">{status.error.message}</p>}
      {data && (
        <>
          <section className="card stack">
            <h2>Impostazioni dell’ambiente</h2>
            {!data.configured && (
              <p className="notice">
                Questo ambiente non ha ancora tutte le impostazioni di SharePoint.
              </p>
            )}
            <dl className="facts">
              <Fact label="ID tenant" value={data.settings.tenantId} />
              <Fact
                label="ID applicazione (K-Management Documenti)"
                value={data.settings.clientId}
              />
              <Fact label="Sito" value={data.settings.site} />
              <Fact label="Raccolta" value={data.settings.library} />
            </dl>
          </section>

          <section className="card stack">
            <h2>Da configurare in Entra</h2>
            <p className="muted small">
              Registrazioni app → K-Management Documenti → Certificati e segreti → Credenziali
              federate → Aggiungi credenziale → scenario «Altro emittente».
            </p>
            {data.identityError && <p className="notice">{data.identityError}</p>}
            <dl className="facts">
              <Fact label="Emittente (Issuer)" value={data.federatedCredential.issuer} copy />
              <Fact
                label="Identificatore soggetto (Subject)"
                value={data.federatedCredential.subject}
                copy
              />
              <Fact label="Destinatari (Audience)" value={data.federatedCredential.audience} copy />
              {data.identity?.email && (
                <Fact label="Account di servizio del server" value={data.identity.email} />
              )}
            </dl>
          </section>

          <section className="card stack">
            <div className="row between">
              <h2>Verifica del collegamento</h2>
              <button
                type="button"
                className="primary"
                disabled={check.isPending}
                onClick={() => check.mutate()}
              >
                {check.isPending ? 'Verifica in corso…' : 'Verifica collegamento'}
              </button>
            </div>
            {check.isError && <p className="error">{check.error.message}</p>}
            {check.data && (
              <>
                <p className={check.data.ok ? 'notice info' : 'notice'}>
                  {check.data.ok
                    ? 'Collegamento riuscito: il server entra nel sito K-Apps e legge la sua raccolta.'
                    : 'Il collegamento non è ancora completo: il primo passaggio non riuscito dice cosa manca.'}
                </p>
                <ol className="check-steps">
                  {check.data.steps.map((step) => (
                    <li key={step.key} className={step.ok ? 'ok' : 'failed'}>
                      <span className="check-mark" aria-hidden="true">
                        {step.ok ? '✓' : '✕'}
                      </span>
                      <span>
                        <strong>{step.label}</strong>
                        <small className="muted block">{step.detail}</small>
                      </span>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </section>
        </>
      )}
    </section>
  );
}

function Fact({ label, value, copy }: { label: string; value: string | null; copy?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {value ? <code className="code">{value}</code> : <span className="muted">—</span>}
        {copy && value && (
          <button
            type="button"
            className="link small"
            onClick={() => {
              void navigator.clipboard
                .writeText(value)
                .then(() => setCopied(true))
                .catch(() => setCopied(false));
            }}
          >
            {copied ? 'copiato' : 'copia'}
          </button>
        )}
      </dd>
    </div>
  );
}
